import React, { Children, lazy } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HeartPulse, Users, Shield, Eye, Activity, Plane, Briefcase, ArrowRight } from 'lucide-react';
const coverages = [{
  title: 'Medicare Insurance',
  description: 'Navigate Parts A, B, C, and D with confidence. We help you find the right Advantage or Supplement plan.',
  icon: HeartPulse,
  link: '/insurance/medicare',
  image: 'https://images.unsplash.com/photo-1447069387593-a5de0862481e?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
  imageAlt: 'Senior couple smiling together outdoors'
}, {
  title: 'Individual & Family Health',
  description: "Affordable ACA and private health plans tailored to your family's specific medical needs and budget.",
  icon: Users,
  link: '/insurance/health',
  image: 'https://images.unsplash.com/photo-1609220136736-443140cffec6?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
  imageAlt: 'Happy family of four playing together at home'
}, {
  title: 'Life Insurance',
  description: "Protect your loved ones' financial future with term, whole, or universal life insurance policies.",
  icon: Shield,
  link: '/insurance/life',
  image: 'https://images.unsplash.com/photo-1581952976147-5a2d15560349?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
  imageAlt: 'Parents embracing their young children'
}, {
  title: 'Dental & Vision',
  description: "Keep your smile bright and vision clear with standalone plans that cover what health insurance doesn't.",
  icon: Eye,
  link: '/insurance/dental-vision',
  image: 'https://images.unsplash.com/photo-1606811971618-4486d14f3f99?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
  imageAlt: 'Person smiling brightly during dental checkup'
}, {
  title: 'Disability Insurance',
  description: 'Protect your income if an illness or injury prevents you from working. Short and long-term options.',
  icon: Activity,
  link: '/insurance/disability',
  image: 'https://images.unsplash.com/photo-1573497019418-b400bb3ab074?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
  imageAlt: 'Professional working confidently at desk'
}, {
  title: 'Travel Medical',
  description: 'Stay protected abroad with comprehensive medical coverage for international trips and vacations.',
  icon: Plane,
  link: '/insurance/travel',
  image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
  imageAlt: 'Traveler with passport and suitcase ready for trip'
}, {
  title: 'Employer Group Benefits',
  description: 'Attract and retain top talent with competitive, cost-effective health benefits for your employees.',
  icon: Briefcase,
  link: '/insurance/employer-group',
  image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80',
  imageAlt: 'Diverse team collaborating in a modern office'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function CoverageGrid() {
  return <section id="coverage" className="py-24 bg-surface" data-id="element-35">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-36">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-37">
          <span className="inline-block text-sm font-semibold uppercase tracking-wider text-accent mb-3" data-id="element-38">
            Our Coverage
          </span>
          <h2 className="text-4xl font-bold mb-4" data-id="element-39">
            Comprehensive coverage for every stage of life.
          </h2>
          <p className="text-lg text-slate-600" data-id="element-40">
            Whether you're turning 65, starting a family, or running a business,
            we have the expertise to find the right protection for you.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-41">
          {coverages.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={index} variants={itemVariants} className="h-full" data-id="element-42">
                <Link to={item.link} className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-float transition-all duration-300 hover:-translate-y-1 flex flex-col h-full overflow-hidden" data-id="element-43">
                  {/* Image */}
                  <div className="relative h-44 overflow-hidden bg-navy/5" data-id="element-44">
                    <img src={item.image} alt={item.imageAlt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-45" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-navy/0 to-transparent" data-id="element-46" />
                    {/* Floating icon badge */}
                    <div className="absolute bottom-3 left-3 w-11 h-11 bg-white rounded-xl shadow-md flex items-center justify-center" data-id="element-47">
                      <Icon className="w-5 h-5 text-navy" data-id="element-48" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col flex-grow p-6" data-id="element-49">
                    <h3 className="text-lg font-bold text-navy mb-2" data-id="element-50">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-5 flex-grow" data-id="element-51">
                      {item.description}
                    </p>
                    <div className="mt-auto flex items-center text-sm font-bold text-accent group-hover:text-accent-hover transition-colors" data-id="element-52">
                      Learn more
                      <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-53" />
                    </div>
                  </div>
                </Link>
              </motion.div>;
        })}

          {/* CTA Card */}
          <motion.div variants={itemVariants} className="bg-navy rounded-2xl shadow-soft flex flex-col h-full relative overflow-hidden" data-id="element-54">
            <img src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80" alt="Licensed insurance agent ready to help" className="absolute inset-0 w-full h-full object-cover opacity-20" data-id="element-55" />
            <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/90 to-navy-dark/95" data-id="element-56" />
            <div className="relative z-10 p-7 flex flex-col h-full justify-center" data-id="element-57">
              <div className="w-11 h-11 bg-accent rounded-xl flex items-center justify-center mb-5" data-id="element-58">
                <Shield className="w-5 h-5 text-navy-dark" data-id="element-59" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-60">
                Not sure what you need?
              </h3>
              <p className="text-slate-200 text-sm mb-6 leading-relaxed" data-id="element-61">
                Talk to a licensed KRT agent today for a free, no-obligation
                consultation.
              </p>
              <button className="bg-accent hover:bg-accent-hover text-navy-dark font-bold py-2.5 px-6 rounded-full transition-colors w-full" data-id="element-62">
                Get Expert Advice
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}